import { ApiHttpError } from '@/shared/api/client';
import { ZodError } from 'zod';
import {
  LOCAL_UPLOAD_STATUS_INITIAL_DELAY_MS,
  LOCAL_UPLOAD_STATUS_POLL_MS,
  SOURCE_IMPORT_STATUS_INITIAL_DELAY_MS,
  SOURCE_IMPORT_STATUS_POLL_MS,
} from '../config/constants';
import {
  MAX_STATUS_IDS_PER_REQUEST,
  MISSING_STATUS_REASON,
  STATUS_POLL_BACKOFF_CEILING_MS,
  STATUS_POLL_JITTER_MS,
} from '../config/statusCoordinator';
import { deriveLocalStatusPatch, deriveStatusPatch } from '../lib/importProjection';
import { isTerminalPhase, uploadActions } from '../stores/uploadStore';
import type {
  LocalSubmissionStatusItem,
  SourceImportStatusItem,
  UploadTask,
} from '../types/uploads';
import { fetchSourceImportsStatus } from './sourceImportService';
import { fetchLocalSubmissionsStatus } from './submissionService';

interface TrackedItem {
  backendId: string;
  mediaType?: 'video' | 'mcap';
}

interface LaneOptions<TStatus> {
  label: 'source import' | 'local upload';
  fetchStatus: (backendIds: string[]) => Promise<TStatus[]>;
  statusId: (status: TStatus) => string;
  derivePatch: (status: TStatus, item: UploadTask, tracking: TrackedItem) => Partial<UploadTask>;
  pollMs: number;
  initialDelayMs: number;
  autoPoll: boolean;
  random: () => number;
}

interface StatusLane {
  track(itemId: string, tracking: TrackedItem): void;
  untrack(itemId: string): void;
  refresh(itemId: string): void;
  poll(): Promise<void>;
  reset(): void;
  stop(): void;
  trackedItemIds(): string[];
  isTracking(itemId: string): boolean;
  consecutiveFailures(): number;
}

export interface StatusCoordinatorOptions {
  /** Source-import transport override (tests). */
  fetchSourceStatus?: (importIds: string[]) => Promise<SourceImportStatusItem[]>;
  /** Local-upload transport override (tests). */
  fetchLocalStatus?: (sessionIds: string[]) => Promise<LocalSubmissionStatusItem[]>;
  sourcePollMs?: number;
  localPollMs?: number;
  sourceInitialDelayMs?: number;
  localInitialDelayMs?: number;
  /** Drive each lane's internal `setTimeout` loop. Off in unit tests that call `poll()` directly. */
  autoPoll?: boolean;
  /** Jitter source (tests seed a deterministic value). */
  random?: () => number;
}

export interface StatusCoordinator {
  /** Route `im-*` to source status and `up-*` to local status. The two ids never share a request. */
  track(itemId: string, backendId: string, mediaType?: 'video' | 'mcap'): void;
  untrack(itemId: string): void;
  /** Retry only status reconciliation after a permanent 4xx; never replay upload bytes. */
  refresh(itemId: string): void;
  /** Run one single-flight pass in both lanes; primarily exposed for tests/diagnostics. */
  poll(): Promise<void>;
  reset(): void;
  stop(): void;
  trackedItemIds(): string[];
  isTracking(itemId: string): boolean;
  consecutiveFailures(): number;
}

function chunk<T>(items: T[], size: number): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) out.push(items.slice(i, i + size));
  return out;
}

function isPermanentStatusError(error: unknown): error is ApiHttpError | ZodError {
  if (error instanceof ZodError) return true;
  return (
    error instanceof ApiHttpError &&
    error.status >= 400 &&
    error.status < 500 &&
    error.status !== 408 &&
    error.status !== 429
  );
}

function statusSyncReason(error: ApiHttpError | ZodError): string {
  if (error instanceof ApiHttpError) {
    return `Status sync rejected (HTTP ${error.status}). Refresh status to try again.`;
  }
  return 'Status response did not match the expected contract. Refresh status to try again.';
}

/** Refresh the target project's asset list so a newly accepted transfer appears in raw footage. */
async function invalidateProjectAssets(projectId: string): Promise<void> {
  const [{ queryClient }, { RAW_FOOTAGE_KEY }] = await Promise.all([
    import('@/app/queryClient'),
    import('@/features/raw-footage/config/constants'),
  ]);
  void queryClient.invalidateQueries({
    queryKey: [RAW_FOOTAGE_KEY, 'project', projectId, 'assets'],
  });
}

function createStatusLane<TStatus>(options: LaneOptions<TStatus>): StatusLane {
  const tracked = new Map<string, TrackedItem>();
  const suspended = new Set<string>();
  let inFlight: Promise<void> | null = null;
  let timer: ReturnType<typeof setTimeout> | null = null;
  let failures = 0;

  function stop(): void {
    if (timer) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function activeCount(): number {
    let count = 0;
    for (const itemId of tracked.keys()) if (!suspended.has(itemId)) count += 1;
    return count;
  }

  function scheduleNext(ms: number): void {
    stop();
    timer = setTimeout(() => {
      timer = null;
      void tick();
    }, ms);
  }

  function ensureRunning(delayMs = options.initialDelayMs): void {
    if (!options.autoPoll || timer || inFlight || activeCount() === 0) return;
    scheduleNext(delayMs);
  }

  async function tick(): Promise<void> {
    await poll();
    if (!options.autoPoll || activeCount() === 0) return;
    const base =
      failures > 0
        ? Math.min(options.pollMs * 2 ** failures, STATUS_POLL_BACKOFF_CEILING_MS)
        : options.pollMs;
    scheduleNext(base + Math.floor(options.random() * STATUS_POLL_JITTER_MS));
  }

  function prune(): void {
    for (const itemId of [...tracked.keys()]) {
      const item = uploadActions.get(itemId);
      if (!item || isTerminalPhase(item.phase)) {
        tracked.delete(itemId);
        suspended.delete(itemId);
      }
    }
  }

  function stageStatusPatch(
    itemId: string,
    tracking: TrackedItem,
    status: TStatus,
    patches: Array<{ id: string; patch: Partial<UploadTask> }>,
    acceptedProjectIds: Set<string>,
  ): void {
    const item = uploadActions.get(itemId);
    if (!item) {
      tracked.delete(itemId);
      suspended.delete(itemId);
      return;
    }
    const patch = options.derivePatch(status, item, tracking);
    patches.push({
      id: itemId,
      patch: { statusSyncError: undefined, reason: undefined, ...patch },
    });
    suspended.delete(itemId);
    if (patch.phase && isTerminalPhase(patch.phase)) {
      tracked.delete(itemId);
      if (patch.phase === 'accepted') acceptedProjectIds.add(item.target.projectId);
    }
  }

  function stageSuspendedIds(
    itemIdsByBackendId: Map<string, string[]>,
    ids: string[],
    reason: string,
    patches: Array<{ id: string; patch: Partial<UploadTask> }>,
  ) {
    for (const backendId of ids) {
      for (const itemId of itemIdsByBackendId.get(backendId) ?? []) {
        if (!tracked.has(itemId)) continue;
        suspended.add(itemId);
        patches.push({
          id: itemId,
          patch: {
            phase: 'error',
            reason,
            statusSyncError: true,
            needsReattach: undefined,
          },
        });
      }
    }
  }

  async function doPoll(): Promise<void> {
    prune();
    if (activeCount() === 0) return;

    const itemIdsByBackendId = new Map<string, string[]>();
    for (const [itemId, tracking] of tracked) {
      if (suspended.has(itemId)) continue;
      const ids = itemIdsByBackendId.get(tracking.backendId);
      if (ids) ids.push(itemId);
      else itemIdsByBackendId.set(tracking.backendId, [itemId]);
    }
    const batches = chunk([...itemIdsByBackendId.keys()], MAX_STATUS_IDS_PER_REQUEST);
    const results = await Promise.allSettled(batches.map((ids) => options.fetchStatus(ids)));
    const patches: Array<{ id: string; patch: Partial<UploadTask> }> = [];
    const acceptedProjectIds = new Set<string>();
    let transientFailure = false;

    for (const [index, result] of results.entries()) {
      if (result.status === 'rejected') {
        if (isPermanentStatusError(result.reason)) {
          stageSuspendedIds(
            itemIdsByBackendId,
            batches[index],
            statusSyncReason(result.reason),
            patches,
          );
          console.warn(
            `[uploads] ${options.label} status poll rejected; waiting for manual refresh`,
            result.reason,
          );
        } else {
          transientFailure = true;
          console.warn(
            `[uploads] ${options.label} status poll failed; backing off and retrying`,
            result.reason,
          );
        }
        continue;
      }

      const returnedIds = new Set<string>();
      for (const status of result.value) {
        const backendId = options.statusId(status);
        returnedIds.add(backendId);
        const itemIds = itemIdsByBackendId.get(backendId);
        if (!itemIds) continue;
        for (const itemId of itemIds) {
          const tracking = tracked.get(itemId);
          if (tracking) {
            stageStatusPatch(itemId, tracking, status, patches, acceptedProjectIds);
          }
        }
      }
      const missingIds = batches[index].filter((backendId) => !returnedIds.has(backendId));
      if (missingIds.length > 0) {
        stageSuspendedIds(itemIdsByBackendId, missingIds, MISSING_STATUS_REASON, patches);
        console.warn(
          `[uploads] ${options.label} status response omitted requested ids; waiting for manual refresh`,
          missingIds,
        );
      }
    }

    uploadActions.patchMany(patches);
    for (const projectId of acceptedProjectIds) void invalidateProjectAssets(projectId);
    failures = transientFailure ? failures + 1 : 0;
  }

  function poll(): Promise<void> {
    if (inFlight) return inFlight;
    inFlight = doPoll().finally(() => {
      inFlight = null;
    });
    return inFlight;
  }

  function track(itemId: string, tracking: TrackedItem): void {
    tracked.set(itemId, tracking);
    if (uploadActions.get(itemId)?.statusSyncError) suspended.add(itemId);
    else suspended.delete(itemId);
    ensureRunning();
  }

  function untrack(itemId: string): void {
    tracked.delete(itemId);
    suspended.delete(itemId);
    if (activeCount() === 0) stop();
  }

  function refresh(itemId: string): void {
    if (!tracked.has(itemId)) return;
    suspended.delete(itemId);
    failures = 0;
    ensureRunning(0);
  }

  function reset(): void {
    tracked.clear();
    suspended.clear();
    failures = 0;
    stop();
  }

  return {
    track,
    untrack,
    refresh,
    poll,
    reset,
    stop,
    trackedItemIds: () => [...tracked.keys()],
    isTracking: (itemId) => tracked.has(itemId),
    consecutiveFailures: () => failures,
  };
}

function createStatusCoordinator(options: StatusCoordinatorOptions = {}): StatusCoordinator {
  const autoPoll = options.autoPoll ?? true;
  const random = options.random ?? Math.random;
  const sourceLane = createStatusLane<SourceImportStatusItem>({
    label: 'source import',
    fetchStatus: options.fetchSourceStatus ?? fetchSourceImportsStatus,
    statusId: (status) => status.importId,
    derivePatch: (status, item, tracking) =>
      deriveStatusPatch(status, {
        channel: item.channel,
        mediaType: tracking.mediaType ?? item.mediaType,
      }),
    pollMs: options.sourcePollMs ?? SOURCE_IMPORT_STATUS_POLL_MS,
    initialDelayMs: options.sourceInitialDelayMs ?? SOURCE_IMPORT_STATUS_INITIAL_DELAY_MS,
    autoPoll,
    random,
  });
  const localLane = createStatusLane<LocalSubmissionStatusItem>({
    label: 'local upload',
    fetchStatus: options.fetchLocalStatus ?? fetchLocalSubmissionsStatus,
    statusId: (status) => status.sessionId,
    derivePatch: (status, item, tracking) =>
      deriveLocalStatusPatch(status, tracking.mediaType ?? item.mediaType),
    pollMs: options.localPollMs ?? LOCAL_UPLOAD_STATUS_POLL_MS,
    initialDelayMs: options.localInitialDelayMs ?? LOCAL_UPLOAD_STATUS_INITIAL_DELAY_MS,
    autoPoll,
    random,
  });

  function laneFor(backendId: string): StatusLane | null {
    if (backendId.startsWith('im-')) return sourceLane;
    if (backendId.startsWith('up-')) return localLane;
    return null;
  }

  function track(itemId: string, backendId: string, mediaType?: 'video' | 'mcap'): void {
    const lane = laneFor(backendId);
    if (!lane) {
      uploadActions.patch(itemId, {
        phase: 'error',
        reason: 'Status sync rejected: unsupported backend id.',
        statusSyncError: true,
        needsReattach: undefined,
      });
      return;
    }
    lane.track(itemId, { backendId, mediaType });
  }

  function untrack(itemId: string): void {
    sourceLane.untrack(itemId);
    localLane.untrack(itemId);
  }

  function refresh(itemId: string): void {
    let lane = sourceLane.isTracking(itemId)
      ? sourceLane
      : localLane.isTracking(itemId)
        ? localLane
        : null;
    if (!lane) {
      const item = uploadActions.get(itemId);
      if (!item?.backendId) return;
      lane = laneFor(item.backendId);
      lane?.track(itemId, { backendId: item.backendId, mediaType: item.mediaType });
    }
    lane?.refresh(itemId);
  }

  return {
    track,
    untrack,
    refresh,
    poll: async () => {
      await Promise.all([sourceLane.poll(), localLane.poll()]);
    },
    reset: () => {
      sourceLane.reset();
      localLane.reset();
    },
    stop: () => {
      sourceLane.stop();
      localLane.stop();
    },
    trackedItemIds: () => [...sourceLane.trackedItemIds(), ...localLane.trackedItemIds()],
    isTracking: (itemId) => sourceLane.isTracking(itemId) || localLane.isTracking(itemId),
    consecutiveFailures: () =>
      Math.max(sourceLane.consecutiveFailures(), localLane.consecutiveFailures()),
  };
}

/** App-wide singleton above the router. Auth switching resets both UID-scoped lanes. */
export const uploadStatusCoordinator = createStatusCoordinator();
